import {
  resourceIdFilter,
  tagFilter,
  type FilterDefinition,
} from "@/shared/components/ResourceView";
import { buildCustomFieldFilterDefinitions } from "@/shared/components/ResourceView/customFields/customFieldGridBuilders";
import type { CustomFieldRecord } from "@/shared/domain/customFields";

/**
 * Entities related to a metric data point whose custom fields the dashboard
 * can filter on, addressed on the wire as `<entity>.custom.<name>`.
 *
 * A session belongs to any number of collections, so a collection row filters
 * the session's collection set rather than one related record: a positive
 * comparator matches sessions in at least one collection satisfying it, and a
 * negated comparator (NOT_EQUALS, IS_NULL, ...) matches sessions in no
 * collection satisfying its positive form, including sessions in no
 * collection at all.
 */
export type DashboardCustomFieldEntity = "collection" | "device" | "session";

/**
 * Related-entity property filters for the metrics dashboard. Each `field` is
 * already the entity-prefixed wire path the metrics condition API requires
 * (`session.` / `device.` / `collection.`) — unlike the resource views' bare
 * roboQL tokens — so `filterToCondition` passes it through with no adapter.
 *
 * Each label IS the field token ("session.name", "device.device_id"): the
 * picker is one flat list mixing fields of several entities, so an
 * unqualified "Name" or "Tags" would not say whose property it filters, and
 * showing the wire path keeps the label and the token trivially in sync.
 * Listed alphabetically by token, which — because every token leads with its
 * entity — also keeps each entity's fields together; the menu preserves this
 * ordering after custom fields merge in (see {@link sortByFieldToken}).
 *
 * The backend's metric-filter classifier (`metrics_filter_model.py`) admits
 * more references than this list offers: the audit fields (`created`,
 * `created_by`, `modified`, `modified_by`) are deliberately not offered, and
 * a `metadata` reference names a JSON key (`metadata.<key>`), which a fixed
 * picker row cannot offer. Collection rows are the opposite case: membership
 * (`collection.collection_id`) is the only native collection field the
 * classifier admits, so the single row here is the complete native set, and
 * the other collection filters are custom fields (see
 * {@link buildDashboardCustomFieldFilterDefinitions}). Comparator
 * restrictions come from the shared presets ({@link resourceIdFilter} for id
 * rows, {@link tagFilter} for tag rows), whose parity tests check them
 * against the RoboQL catalog; the unrestricted string rows and the
 * date/numeric rows offer their type's full comparator set, all of which the
 * catalog lists for those fields.
 */
export const DASHBOARD_PROPERTY_FILTERS: FilterDefinition[] = [
  {
    type: "string",
    field: "collection.collection_id",
    name: "collection.collection_id",
    ...resourceIdFilter,
  },
  {
    // The one preset application narrower than its catalog: the metrics
    // backend resolves this reference in the Devices catalog, where
    // `device_id` also admits substring and presence operators. Exact match
    // is still all this row offers — the id is opaque — so
    // `resourceIdFilter.test.ts` asserts it as a subset, not an equality.
    type: "string",
    field: "device.device_id",
    name: "device.device_id",
    ...resourceIdFilter,
  },
  {
    type: "string",
    field: "device.tags",
    name: "device.tags",
    ...tagFilter,
  },
  { type: "numeric", field: "session.duration", name: "session.duration" },
  { type: "date", field: "session.end_time", name: "session.end_time" },
  {
    type: "string",
    field: "session.id",
    name: "session.id",
    ...resourceIdFilter,
  },
  { type: "string", field: "session.name", name: "session.name" },
  { type: "date", field: "session.start_time", name: "session.start_time" },
  {
    type: "string",
    field: "session.tags",
    name: "session.tags",
    ...tagFilter,
  },
];

/**
 * Order filter definitions alphabetically by their wire field token. Every
 * token leads with its entity (`collection.`, `device.`, `session.`), so the
 * alphabetical order also keeps each entity's fields — native and custom
 * alike — together in the picker. Applied to the merged native + custom list,
 * since the custom-field definitions arrive from async queries and would
 * otherwise trail the natives in load order.
 */
export function sortByFieldToken(
  definitions: FilterDefinition[],
): FilterDefinition[] {
  return [...definitions].sort((a, b) => a.field.localeCompare(b.field, "en"));
}

/**
 * Custom-field filters for one related entity, addressed as
 * `<entity>.custom.<name>` for the wire. Reuses the resource-view builder so
 * the two can't drift apart. Like the native rows above, each label is the
 * wire path itself rather than the user-authored display name: a field named
 * `region` on two entity types would otherwise render as two identical
 * picker rows.
 */
export function buildDashboardCustomFieldFilterDefinitions(
  readyFields: CustomFieldRecord[],
  entity: DashboardCustomFieldEntity,
): FilterDefinition[] {
  return buildCustomFieldFilterDefinitions(readyFields, `${entity}.`).map(
    (definition) => ({
      ...definition,
      name: definition.field,
    }),
  );
}
